/**
 * The Become a Mystic gate.
 *
 * The server has always computed `become_mystic_unlocked` from the eight
 * Phase-1 quests and shipped it to the FE, where MysticQuestPanel assigned it
 * to a ref it never read. The bottom-nav Studio tab was meanwhile gated on
 * nothing but being signed in, so every seeker saw it and walked straight into
 * the become-a-Mystic wizard.
 *
 * Pulled out of both components so the rule is testable, and so the two places
 * that need it cannot drift apart.
 */

/**
 * Should the Studio tab be in the bottom nav?
 *
 * The critical case is `isReader === true`: an existing Mystic must never be
 * locked out of their own Studio, and their gating quests may well be undone —
 * they may have become a Mystic before the quests existed, or by buying past
 * them. `isReader` is null until /my-reader resolves, so it is compared
 * strictly rather than for truthiness.
 */
export function showStudioTab({ querentId, isReader, mysticUnlocked } = {}) {
  return !!querentId && (isReader === true || !!mysticUnlocked)
}

/**
 * May this Mystic open their Studio?
 *
 * Qualification (the eight quests) gets you into the wizard. The SEAT is what
 * keeps the Studio open, and it lapses if renewal cannot be paid. A lapse
 * closes access and keeps every scrap of their work — coming back is a
 * top-up, not a rebuild.
 *
 * Someone who is not yet a Mystic passes: they are on their way INTO the
 * wizard, which is free to walk and ends at the paywall.
 */
export function canOpenStudio({ isReader, seatActive } = {}) {
  if (isReader !== true) return true
  return !!seatActive
}

/**
 * What the Studio panel shows while, or after, /my-reader resolves.
 *
 * `isReader` is null until the lookup answers. It used to stay null on any
 * answer but 200/404, and the panel showed "Loading your studio…" forever: a
 * Mystic whose stored token had expired (prod, 2026-09-17: my-reader 401)
 * looked signed in everywhere else, and the Studio just spun.
 *
 *   'reader'   -- the Studio itself
 *   'wizard'   -- not a Mystic yet (404): Become a Mystic
 *   'loading'  -- no answer yet
 *   'expired'  -- 401/403: the sign-in is no longer good; sign in again
 *   'failed'   -- anything else (500, network): offer a retry
 */
export function studioGateState({ isReader, lookupStatus } = {}) {
  if (isReader === true) return 'reader'
  if (isReader === false) return 'wizard'
  if (lookupStatus === 401 || lookupStatus === 403) return 'expired'
  if (lookupStatus) return 'failed'
  return 'loading'
}
